/**
 * Executes a widget action based on its type.
 * Receives the action object from the contract and context callbacks.
 *
 * Action types:
 * - NAVIGATE: calls onNavigate with the target path
 * - SLIDEOUT: calls onSlideout with the target component name and params
 * - POPUP: calls onPopup with the target component name and params
 * - API: calls onRefresh (local widget refresh) when no endpoint, otherwise calls onApiAction
 *
 * @param {Object} action - Action object from the contract { type, target, endpoint, method, params_from, ... }
 * @param {Object} handlers - Available handler functions from context/wrapper
 * @param {Function} handlers.onNavigate - Navigation handler
 * @param {Function} [handlers.onSlideout] - Slideout panel handler
 * @param {Function} [handlers.onPopup] - Modal popup handler
 * @param {Function} [handlers.onRefresh] - Widget data refresh handler
 * @param {Function} [handlers.onApiAction] - API call handler
 * @param {Object} [context] - Additional context (row data, selection, scope)
 */
export const executeAction = (action, handlers = {}, context = {}) => {
  if (!action?.type) return;

  switch (action.type) {
    case 'NAVIGATE': {
      handlers.onNavigate?.(action.target);
      break;
    }
    case 'SLIDEOUT': {
      if (handlers.onSlideout) {
        handlers.onSlideout(action.target, resolveParams(action, context));
      } else {
        handlers.onWidgetAction?.({ ...action, params: resolveParams(action, context) });
      }
      break;
    }
    case 'POPUP': {
      if (handlers.onPopup) {
        handlers.onPopup(action.target, resolveParams(action, context));
      } else {
        handlers.onWidgetAction?.({ ...action, params: resolveParams(action, context) });
      }
      break;
    }
    case 'API': {
      if (!action.endpoint) {
        handlers.onRefresh?.();
      } else {
        handlers.onApiAction?.(action);
      }
      break;
    }
    default:
      break;
  }
};

const resolveParams = (action, context) => {
  switch (action.params_from) {
    case 'ROW':
      return context.rowData || {};
    case 'SELECTION':
      return context.selection || {};
    case 'CONTEXT':
      return context.scopeContext || {};
    case 'STATIC':
      return action.static_params || {};
    default:
      return {};
  }
};
